import chunk from 'lodash/chunk';
import cloneDeep from 'lodash/cloneDeep';
import Papa from 'papaparse';
import { flexRender } from '@tanstack/react-table';

import {
  formatData,
  getThresholdTier,
  isSuppressedTier,
  isValueColumnFormat,
} from '../../../helpers';

/**
 * gets csv output usable in excel/sheets
 * @param  {Object}  options.rangeEndCell
 * @param  {Object}  options.selectedCell
 * @param  {Object}  options.table
 * @return {String}  csv output
 */
export const copySelectedCellValues = ({ rangeEndCell, selectedCell, table }) => {
  const cellsSelected = getSelectedCellRange({
    endCell: rangeEndCell,
    startCell: selectedCell,
    table,
  });
  const keys = Object.keys(cellsSelected);

  if (keys.length === 0) {
    return navigator.clipboard.writeText(getCellText(selectedCell));
  }

  const firstRowIndex = parseInt(keys[0].split('_')[0]);
  const lastRowIndex = parseInt(keys[keys.length - 1].split('_')[0]);
  const numberOfRows = lastRowIndex - firstRowIndex + 1;

  const values = keys.map((key) => getCellText(cellsSelected[key]));
  const text = Papa.unparse(
    {
      data: chunk(values, keys.length / numberOfRows),
    },
    { delimiter: '\t' }
  );

  return navigator.clipboard.writeText(text);
};

/**
 * gets csv output usable in excel/sheets
 * @param  {Array}   options.columns
 * @param  {Array}   options.data
 * @return {String} csv output
 */
const getAccessorKeys = (cell) => {
  const accessor =
    cell.column.columnDef?.accessorKey || cell.column.columnDef?.accessor || cell.column.id || '';

  return accessor.split(';').filter(Boolean);
};

const getCellText = (cell) => {
  const rendered = flexRender(cell.column.columnDef.cell, cell.getContext());
  const renderedText = extractTextContent(rendered).trim();

  if (renderedText.length > 0) {
    return renderedText;
  }

  const rawValue = cell.getValue();
  if (rawValue !== null && rawValue !== undefined && rawValue !== 'NAN_SENTINEL') {
    return formatData({
      format: cell.column.columnDef?.format,
      value: rawValue,
    });
  }

  const accessorKeys = getAccessorKeys(cell);
  let valueToUse;

  for (const accessor of accessorKeys) {
    const candidate = cell.row.original?.[accessor];
    if (candidate === null || candidate === undefined || candidate === 'NAN_SENTINEL') {
      break;
    }
    valueToUse = candidate;
  }

  if (valueToUse !== undefined) {
    return formatData({
      format: cell.column.columnDef?.format,
      value: valueToUse,
    });
  }

  return formatData({
    format: cell.column.columnDef?.format,
    value: 'NAN_SENTINEL',
  });
};

const extractTextContent = (node) => {
  if (node === null || node === undefined || node === false) return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) {
    return node.map((child) => extractTextContent(child)).join('');
  }
  if (typeof node === 'object' && 'props' in node && node.props) {
    return extractTextContent(node.props.children);
  }
  return '';
};

const getHeaderText = (column) => {
  const value =
    typeof column.header === 'function'
      ? column.header({ column })
      : (column.header ?? column.Header);

  const text = extractTextContent(value);
  return text || column.accessorKey || column.id || '';
};

const getFooterText = (column) => {
  const value =
    typeof column.footer === 'function'
      ? column.footer({ column })
      : (column.footer ?? column.Footer);

  return extractTextContent(value);
};

/**
 * gets csv output usable in excel/sheets
 * @param  {Array}   options.columns
 * @param  {Array}   options.data
 * @return {String} csv output
 */
export const getCopyTableValue = ({ columns, data }) => {
  const headerGroups = columns.filter((column) => Array.isArray(column?.columns));

  if (headerGroups?.length > 0) {
    columns = [];

    headerGroups.forEach((headerGroup) => {
      columns = columns.concat(headerGroup.columns);
    });
  }

  const footerValues = columns.map((column) => getFooterText(column));
  const headerAccessors = columns.map((column) => column.accessorKey);
  const headerNames = columns.map((column) => getHeaderText(column));
  const rows = cloneDeep(data);

  columns.forEach((column) => {
    const accessors = column?.accessorKey?.split(';') || [column?.accessorKey];
    const format = column?.format;
    // Only value (metric) columns carry a sample tier; leave dimensions alone.
    const isValueColumn = isValueColumnFormat(format);

    rows.forEach((row) => {
      let value;
      let valueAccessor;
      // Walk accessors exactly like the on-screen cell renderer: take the last
      // accessor before the first 'NAN_SENTINEL', so copy and screen agree on
      // which period's value (and paired tier) a ";"-joined column resolves to.
      for (const accessor of accessors) {
        const rawValue = row?.[accessor] ?? row?.original?.[accessor];

        if (rawValue === 'NAN_SENTINEL') {
          break;
        }

        value = rawValue;
        valueAccessor = accessor;
      }

      // Mirror the table: pair the tier with the accessor whose value is shown,
      // then suppressed cells copy as "-" rather than their value.
      const tier = isValueColumn ? getThresholdTier(row, valueAccessor ?? accessors[0]) : undefined;

      row[column?.accessorKey] = formatData({
        format,
        value: isSuppressedTier(tier) ? 'NAN_SENTINEL' : (value ?? 'NAN_SENTINEL'),
      });
    });
  });

  const footer = footerValues.some((value) => value)
    ? '\n' + Papa.unparse({ data: [], fields: footerValues }, { delimiter: '\t', header: true })
    : '';
  const header = Papa.unparse({ data: [], fields: headerNames }, { delimiter: '\t', header: true });
  const parsedData = Papa.unparse(
    { data: rows, fields: headerAccessors },
    { columns, delimiter: '\t', header: false }
  );

  return header + parsedData + footer;
};

/**
 * gets cell below current cell
 * @param  {Object} options.selectedCell
 * @param  {Object} options.table
 * @return {Object}
 */
export const getCellDown = ({ selectedCell, table }) => {
  const rowId = selectedCell?.row?.index;
  const columnName = selectedCell?.id?.split('_').slice(1).join('_');
  const rowsLength = table.getRowModel().rows.length;
  const updatedRowIndex = rowId < rowsLength - 1 ? rowId + 1 : rowsLength - 1;
  const updatedRow = table.getRowModel().rows[updatedRowIndex];
  const newCellId = updatedRowIndex + '_' + columnName;

  return updatedRow.getVisibleCells().find((c) => c.id === newCellId);
};

/**
 * gets cell to the left of current cell
 * @param  {Object} options.selectedCell
 * @return {Object}
 */
export const getCellLeft = ({ selectedCell }) => {
  const cellRows = selectedCell.row.getVisibleCells();
  const columnIndex = cellRows.findIndex((cell) => cell?.id === selectedCell?.id);
  const updatedColumnId = cellRows[columnIndex > 0 ? columnIndex - 1 : 0]?.id?.split('-')[0];

  return cellRows.find((c) => c.id === updatedColumnId);
};

/**
 * gets cell to the right of current cell
 * @param  {Object} options.selectedCell
 * @return {Object}
 */
export const getCellRight = ({ selectedCell }) => {
  const cellRows = selectedCell.row.getVisibleCells();
  const columnsLength = cellRows.length;
  const columnIndex = cellRows.findIndex((cell) => cell?.id === selectedCell?.id);
  const updatedColumnId =
    cellRows[columnIndex < columnsLength - 1 ? columnIndex + 1 : columnsLength - 1]?.id?.split(
      '-'
    )[0];

  return cellRows.find((c) => c.id === updatedColumnId);
};

/**
 * gets cell above current cell
 * @param  {Object} options.selectedCell
 * @param  {Object} options.table
 * @return {Object}
 */
export const getCellUp = ({ selectedCell, table }) => {
  const rowId = selectedCell?.row?.index;
  const columnName = selectedCell?.id?.split('_').slice(1).join('_');
  const updatedRowIndex = rowId > 0 ? rowId - 1 : 0;
  const updatedRow = table.getRowModel().rows[updatedRowIndex];
  const newCellId = updatedRowIndex + '_' + columnName;

  return updatedRow.getVisibleCells().find((c) => c.id === newCellId);
};

/**
 * Calculates all cells between two selected cells
 * @param  {Object} options.endCell
 * @param  {Object} options.startCell
 * @param  {Object} options.table
 * @returns {Object}
 */
const getSelectedCellRange = ({ endCell, startCell, table }) => {
  if (!startCell || !endCell) return {};

  const rows = table.getRowModel().rows;

  const startRowIndex = rows.findIndex((row) => row.id === startCell.row.id);
  const endRowIndex = rows.findIndex((row) => row.id === endCell.row.id); // find index of start and end row
  const rowStart = Math.min(startRowIndex, endRowIndex);
  const rowEnd = Math.max(startRowIndex, endRowIndex); // get top and bottom indicies regardless of direction

  const startColumnIndex = startCell.row.getVisibleCells().findIndex((c) => c.id === startCell.id);
  const endColumnIndex = endCell.row.getVisibleCells().findIndex((c) => c.id === endCell.id); // find index of start and end column
  const columnStart = Math.min(startColumnIndex, endColumnIndex);
  const columnEnd = Math.max(startColumnIndex, endColumnIndex);

  const selected = {};

  for (let r = rowStart; r <= rowEnd; r++) {
    const row = rows[r];
    const cells = row.getVisibleCells();
    for (let c = columnStart; c <= columnEnd; c++) {
      const cell = cells[c];
      cell && (selected[cell.id] = cell);
    }
  }
  return selected;
};

/**
 * gets sort type based on format
 * @param  {String} format
 * @return {String}
 */
export const getSortType = (format) => {
  switch (format) {
    case '+0.0':
    case '0,0':
    case '0,0.0':
    case '0,0.0%':
    case '0.0':
    case '0.0%':
    case '0.00%':
    case '$0,0':
    case '$0,0.00':
      return 'numeric';
    default:
      return 'alphanumeric';
  }
};

/**
 * added sort types to react-table defaults
 * @type {Object}
 */
export const sortTypes = {
  numeric: (rowA, rowB, columnId) => {
    const getNumericValue = (row) => {
      const value = Number(row.getValue(columnId));
      return Number.isFinite(value) ? value : 0;
    };

    return getNumericValue(rowA) - getNumericValue(rowB);
  },
};

export { getSelectedCellRange };

/**
 * Transform columns from react-table v7 format to TanStack v8 format
 * @param {Array} columns - Array of column definitions
 * @return {Array} Transformed columns for TanStack Table
 */
export const transformColumns = (columns) => {
  if (!columns || !Array.isArray(columns)) return [];

  return columns.map((column) => ({
    ...column,
    accessorKey: column.accessor || column.accessorKey,
    cell: column.Cell
      ? (cellProps) => {
          return column.Cell({
            column: cellProps.column,
            getValue: cellProps.getValue,
            row: cellProps.row,
            value: cellProps.getValue(),
          });
        }
      : column.cell ||
        (({ getValue }) => {
          const value = getValue();

          if (value === 'NAN_SENTINEL' || value === null || value === undefined) {
            return '-';
          }

          if (!column.format || column.format === null) {
            return String(value);
          }

          return formatData({
            format: column.format,
            value: value,
          });
        }),
    enableSorting: !column.disableSortBy,
    footer: column.Footer || column.footer,
    format: column.format,
    header: column.Header || column.header,
    id: column.id || column.accessor,
  }));
};
